import validator from 'validator';
import {
  ModiFyTimeSlot,
  ModifySlot,
  SCHEDULE_DAY_ROWS,
  SlotItem,
} from './types';
import { getActiveTimeSlots, isDayActive } from './utils';

export type SlotValidationErrorType =
  | 'empty'
  | 'invalid_format'
  | 'start_gte_end';

const isSlotFieldEmpty = (item: SlotItem): boolean =>
  validator.isEmpty(`${item.hour}`) || validator.isEmpty(`${item.minute}`);

export const getSlotValidationErrorType = (
  slot: ModifySlot,
): SlotValidationErrorType | null => {
  const slotHourMinuteStart = slot.start;
  const slotHourMinuteEnd = slot.end;

  if (
    isSlotFieldEmpty(slotHourMinuteStart) ||
    isSlotFieldEmpty(slotHourMinuteEnd)
  ) {
    return 'empty';
  }

  if (
    slotHourMinuteStart.hour.toString().length > 2 ||
    slotHourMinuteStart.minute.toString().length > 2 ||
    slotHourMinuteEnd.hour.toString().length > 2 ||
    slotHourMinuteEnd.minute.toString().length > 2
  ) {
    return 'invalid_format';
  }

  if (
    parseInt(`${slotHourMinuteStart.hour}`, 10) < 0 ||
    parseInt(`${slotHourMinuteStart.minute}`, 10) < 0 ||
    parseInt(`${slotHourMinuteStart.hour}`, 10) > 23 ||
    parseInt(`${slotHourMinuteStart.minute}`, 10) > 59 ||
    parseInt(`${slotHourMinuteEnd.hour}`, 10) < 0 ||
    parseInt(`${slotHourMinuteEnd.minute}`, 10) < 0 ||
    parseInt(`${slotHourMinuteEnd.hour}`, 10) > 23 ||
    parseInt(`${slotHourMinuteEnd.minute}`, 10) > 59
  ) {
    return 'invalid_format';
  }

  const intervalInMinutesStart =
    parseInt(`${slotHourMinuteStart.hour}`, 10) * 60 +
    parseInt(`${slotHourMinuteStart.minute}`, 10);
  const intervalInMinutesEnd =
    parseInt(`${slotHourMinuteEnd.hour}`, 10) * 60 +
    parseInt(`${slotHourMinuteEnd.minute}`, 10);

  if (intervalInMinutesEnd - intervalInMinutesStart < 1) {
    return 'start_gte_end';
  }

  return null;
};

const getDayLabel = (day: number): string =>
  SCHEDULE_DAY_ROWS.find((row) => row.day === day)?.label || 'this day';

const convertTimeSlotInAbsoluteTime = (slots: ModifySlot[]) => {
  const absoluteValueOfSlots = slots.map((slot: ModifySlot) => {
    const absoluteValueOfStartTime =
      Number(slot.start.hour) * 60 + Number(slot.start.minute);
    const absoluteValueOfEndTime =
      Number(slot.end.hour) * 60 + Number(slot.end.minute);
    return {
      ...slot,
      startAbsoluteValue: absoluteValueOfStartTime,
      endAbsoluteValue: absoluteValueOfEndTime,
    };
  });
  return absoluteValueOfSlots;
};

/** Slots listed in UI order must start at or after the previous slot ends. */
export const validateSlotOrder = (slots: ModifySlot[]): number[] => {
  const outOfOrderIds: number[] = [];

  for (let i = 1; i < slots.length; i += 1) {
    const prev = slots[i - 1];
    const curr = slots[i];

    if (
      getSlotValidationErrorType(prev) !== null ||
      getSlotValidationErrorType(curr) !== null
    ) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const prevEnd =
      parseInt(`${prev.end.hour}`, 10) * 60 +
      parseInt(`${prev.end.minute}`, 10);
    const currStart =
      parseInt(`${curr.start.hour}`, 10) * 60 +
      parseInt(`${curr.start.minute}`, 10);

    if (currStart < prevEnd) {
      outOfOrderIds.push(prev.id, curr.id);
    }
  }

  return Array.from(new Set(outOfOrderIds));
};

export const validateOverlapTimeSlot = (timeSlot: ModiFyTimeSlot): number[] => {
  const validSlots = timeSlot.slots.filter(
    (slot) => getSlotValidationErrorType(slot) === null,
  );

  if (validSlots.length < 2) {
    return [];
  }

  const slotsWithAbsoluteValue = convertTimeSlotInAbsoluteTime(validSlots);

  slotsWithAbsoluteValue.sort((a, b) => {
    if (a.startAbsoluteValue < b.startAbsoluteValue) {
      return -1;
    }
    if (a.startAbsoluteValue > b.startAbsoluteValue) {
      return 1;
    }
    return 0;
  });

  const validationObj = new Set<number>();

  for (let i = 0; i < slotsWithAbsoluteValue.length; i += 1) {
    for (let j = i + 1; j < slotsWithAbsoluteValue.length; j += 1) {
      if (
        slotsWithAbsoluteValue[i].endAbsoluteValue >
        slotsWithAbsoluteValue[j].startAbsoluteValue
      ) {
        validationObj.add(slotsWithAbsoluteValue[i].id);
        validationObj.add(slotsWithAbsoluteValue[j].id);
      }
    }
  }

  return slotsWithAbsoluteValue
    .filter((slot) => validationObj.has(slot.id))
    .map((slot) => slot.id);
};

/** Returns the first validation banner message per PRD priority (one at a time). */
export const getFirstValidationBannerMessage = (
  timeSlots: ModiFyTimeSlot[],
): string | null => {
  const activeDays = getActiveTimeSlots(timeSlots);
  if (activeDays.length === 0) {
    return 'At least one day must be checked';
  }

  for (let i = 0; i < activeDays.length; i += 1) {
    const daySlot = activeDays[i];
    const orderIds = validateSlotOrder(daySlot.slots);
    if (orderIds.length > 0) {
      return 'Each slot must start at or after the previous slot ends.';
    }
    const overlapIds = validateOverlapTimeSlot(daySlot);
    if (overlapIds.length > 0) {
      return 'Each next slot must start after the previous slot ends.';
    }
  }

  for (let i = 0; i < activeDays.length; i += 1) {
    const daySlot = activeDays[i];
    for (let j = 0; j < daySlot.slots.length; j += 1) {
      if (getSlotValidationErrorType(daySlot.slots[j]) === 'empty') {
        return `Please set start and end times for ${getDayLabel(
          daySlot.day,
        )}.`;
      }
    }
  }

  for (let i = 0; i < activeDays.length; i += 1) {
    const daySlot = activeDays[i];
    for (let j = 0; j < daySlot.slots.length; j += 1) {
      if (getSlotValidationErrorType(daySlot.slots[j]) === 'invalid_format') {
        return 'Invalid time format. Please use HH:MM (00:00 - 23:59)';
      }
    }
  }

  for (let i = 0; i < activeDays.length; i += 1) {
    const daySlot = activeDays[i];
    for (let j = 0; j < daySlot.slots.length; j += 1) {
      if (getSlotValidationErrorType(daySlot.slots[j]) === 'start_gte_end') {
        return 'For a time slot the end time must be greater than the start time.';
      }
    }
  }

  return null;
};

export const markSlotValidationErrors = (
  timeSlots: ModiFyTimeSlot[],
): { timeSlots: ModiFyTimeSlot[]; hasError: boolean } => {
  let hasError = false;

  const next = timeSlots.map((timeSlot) => {
    if (!isDayActive(timeSlot)) {
      return timeSlot;
    }

    const orderIds = validateSlotOrder(timeSlot.slots);
    const overlapIds = validateOverlapTimeSlot(timeSlot);
    const slots = timeSlot.slots.map((slot) => {
      const errorType = getSlotValidationErrorType(slot);
      const invalidDataError = errorType !== null;
      const overlapError =
        orderIds.includes(slot.id) || overlapIds.includes(slot.id);
      hasError = hasError || invalidDataError || overlapError;
      return { ...slot, invalidDataError, overlapError };
    });

    return { ...timeSlot, slots };
  });

  return { timeSlots: next, hasError };
};

export const validateField = (
  name: string,
  value: string,
  options?: {
    existingScheduleNames?: string[];
    currentScheduleName?: string;
  },
): string => {
  switch (name) {
    case 'scheduleName': {
      if (validator.isEmpty(value?.trim() || '')) {
        return 'Please provide a schedule name';
      }
      if (!validator.isLength(value, { max: 50 })) {
        return 'Schedule name should be at most 50 characters long';
      }
      const trimmed = value.trim();
      const { existingScheduleNames = [], currentScheduleName } = options || {};
      const isDuplicate = existingScheduleNames.some((existingName) => {
        if (
          currentScheduleName &&
          existingName.trim().toLowerCase() ===
            currentScheduleName.trim().toLowerCase()
        ) {
          return false;
        }
        return existingName.trim().toLowerCase() === trimmed.toLowerCase();
      });
      if (isDuplicate) {
        return 'A schedule with this name already exists';
      }
      break;
    }
    case 'timezone':
      if (validator.isEmpty(value || '')) {
        return 'Please select a timezone';
      }
      break;
    default:
      break;
  }

  return '';
};

export const timeSlotDataValidation = (timeSlotObj: ModifySlot): boolean =>
  getSlotValidationErrorType(timeSlotObj) !== null;
